import { defineStore } from 'pinia';
import { ref, readonly } from 'vue';
import { TianshuEngine } from '../core/tianshu/TianshuEngine';
// We assume CapabilityGateway is available
import CapabilityGateway from '../core/CapabilityGateway';

export const useTopologyStore = defineStore('topology', () => {
  const topology = ref<{ nodes: any[]; links: any[] }>({ nodes: [], links: [] });
  const isFrozen = ref(false);
  const ouroborosGlitch = ref(false);

  // Vector Clock for CRDT (P2P Conflict Resolution)
  const vectorClock = ref<Record<string, number>>({});
  const localNodeId = ref<string>('local_client_' + Math.random().toString(36).substr(2, 9));

  const incrementClock = () => {
    if (!vectorClock.value[localNodeId.value]) {
      vectorClock.value[localNodeId.value] = 0;
    }
    vectorClock.value[localNodeId.value]++;
  };

  const mergeClock = (incomingClock: Record<string, number>) => {
    let hasNewer = false;
    for (const [node, tick] of Object.entries(incomingClock)) {
      if (!vectorClock.value[node] || vectorClock.value[node] < tick) {
        vectorClock.value[node] = tick;
        hasNewer = true;
      }
    }
    return hasNewer;
  };

  // Hydrate tianshu concepts into a node
  const hydrateNodeConcepts = async (node: any) => {
    // [V3] schema v2 回填：properties/metadata 必填（缺省 {}）。2026-09-29 实测：
    // 演示拓扑节点无 properties ⇒ omni_workflow_exec 整包 serde 拒收——任何来源
    // （演示/保存恢复/导入）的节点在入图时统一补齐，Rust 侧 serde(default) 双保险。
    if (!node.properties || typeof node.properties !== 'object') node.properties = {};
    if (!node.metadata || typeof node.metadata !== 'object') node.metadata = {};
    if (node.tianshuKey) {
      // Async parsing of concepts to avoid blocking main thread
      await new Promise(resolve => setTimeout(resolve, 0));
      const c = TianshuEngine.getConcept(node.tianshuKey);
      if (c) {
        node._hydratedConcept = {
          titleKey: c.titleKey,
          descKey: c.descKey,
          philosophyKey: c.philosophyKey,
          scenarioKey: c.scenarioKey,
          telemetry: c.telemetry
        };
      }
    }
    return node;
  };

  /**
   * 拒写窗口（原 Ouroboros 衔尾蛇态）。
   *
   * [2026-09-24 第八轮] 这是一个**有界窗口**，不是一个闩锁 —— 改前它是闩锁，
   * 而全仓没有任何复位点：置 true 的那一处（本函数）之后，`isFrozen` 只被读、
   * 从未被写回 false。它守着的却是**整张拓扑图的写面**
   * （`addNode` / `addLink` / `setTopology` / `updateNode` / `removeNode` / `removeLink`）
   * 以及 `TopologyEngine.executeSync`。
   *
   * 实测路径（`views/director/CanvasTopologyBoard.vue` 的 `handleIntent`）：
   * 意图入口的远程闸 `jarvis_ops_check_entropy` 后端并不存在，而桥对"命令不存在"
   * 是**降级返回 null 而不是抛** ⇒ 那句 `res.is_spam` 抛 TypeError ⇒ 永远走 catch
   * ⇒ catch 里的兜底 `intent.length < 5` 成了唯一生效的判定。
   * 于是：**在指挥台输入 4 个字的指令按回车，本次会话内画布再也无法增删改任何节点**，
   * 而且没有任何提示（写 `ouroborosGlitch` 的消费方、听 `ouroboros-glitch-triggered`
   * 的监听方都不存在）。
   *
   * 现在给窗口封顶并公开解除口：拒绝一次输入的代价是"这几秒内不写入"，
   * 不是"这个会话废了"。
   */
  const OUROBOROS_WINDOW_MS = 2500;
  let ouroborosTimer: ReturnType<typeof setTimeout> | null = null;

  const releaseOuroboros = () => {
    if (ouroborosTimer) {
      clearTimeout(ouroborosTimer);
      ouroborosTimer = null;
    }
    isFrozen.value = false;
    ouroborosGlitch.value = false;
  };

  const triggerOuroboros = (windowMs: number = OUROBOROS_WINDOW_MS) => {
    ouroborosGlitch.value = true;
    isFrozen.value = true;
    if (ouroborosTimer) clearTimeout(ouroborosTimer);
    ouroborosTimer = setTimeout(releaseOuroboros, Math.max(0, windowMs));
    // Dispatch system-level event to unlisten
    window.dispatchEvent(new CustomEvent('ouroboros-glitch-triggered'));
  };

  const addNode = async (node: any) => {
    if (isFrozen.value) return false;
    
    // Check capability gateway
    const canExecute = CapabilityGateway.canExecuteCommand('SPAWN_NODE');
    if (!canExecute) {
      console.warn('CapabilityGateway intercepted node spawn:', node.id);
      return false; // Silently fail or UI could show error
    }

    const hydratedNode = await hydrateNodeConcepts(node);
    topology.value.nodes.push(hydratedNode);
    incrementClock();
    return true;
  };

  const addLink = async (link: any) => {
    if (isFrozen.value) return false;
    topology.value.links.push(link);
    incrementClock();
    return true;
  };

  const setTopology = async (newTopo: any) => {
    if (isFrozen.value) return false;
    if (newTopo.nodes) {
      for (const node of newTopo.nodes) {
        await hydrateNodeConcepts(node);
      }
    }
    topology.value = newTopo;
    incrementClock();
    return true;
  };

  const updateNode = (nodeId: string, updates: any) => {
    if (isFrozen.value) return;
    const node = topology.value.nodes.find(n => n.id === nodeId);
    if (node) {
      Object.assign(node, updates);
      incrementClock();
    }
  };

  const removeNode = (nodeId: string) => {
    if (isFrozen.value) return;
    topology.value.nodes = topology.value.nodes.filter(n => n.id !== nodeId);
    // Also clean up links
    topology.value.links = topology.value.links.filter(
      l => l.source !== nodeId && l.target !== nodeId
    );
    incrementClock();
  };

  const removeLink = (linkId: string) => {
    if (isFrozen.value) return;
    topology.value.links = topology.value.links.filter(l => l.id !== linkId);
    incrementClock();
  };

  return {
    topology, // Keep mutable for UI bindings or use specific actions
    vectorClock: readonly(vectorClock),
    isFrozen: readonly(isFrozen),
    ouroborosGlitch: readonly(ouroborosGlitch),
    triggerOuroboros,
    releaseOuroboros,
    incrementClock,
    mergeClock,
    addNode,
    addLink,
    setTopology,
    updateNode,
    removeNode,
    removeLink
  };
});
